import { Autocomplete, AutocompleteChangeDetails, Button, Checkbox, FormControlLabel, Grid, TextField, Tooltip } from '@mui/material';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomAccordion from '../CustomAccordion/CustomAccordion';
import { setTriggerFixtureSearch, setSearchResultTotalRecord, resetFixtureSearch, setCompanyNamePICData, setFixtureCompanyNamePICSearchKeyword, setTriggerFocusVesselName, setBizTypes, setVesselNames, setCharterers, setOwners, setCollectFroms, setCpDateFrom, setCpDateTo, setStatus, setInvDateFrom, setInvDateTo, setSupports, setBankAccount, setReceivedDateFrom, setReceivedDateTo, setEvaluations, setCommRateBrokers, setInvNo, setShowCloseFile } from './reducers/FixtureSearchReducer';
import FixtureApi, { SearchFixtureParam } from './api/FixtureApi';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import SavedSearch, { ComponentType } from '../SavedSearch/SavedSearch';
import Swal from 'sweetalert2';
import SavedSearchApi, { SaveSavedSearchParam } from '../SavedSearch/api/SavedSearchApi';
import SavedSearchModel from '../SavedSearch/models/SavedSearchModel';
import { addSavedSearch } from '../SavedSearch/reducers/SavedSearchReducer';
import { FixtureSearchSelector, setFixtureSearchResults } from './reducers/FixtureSearchReducer';
import FixtureModel from './models/FixtureModel';
import TagsFilter from '../TagFilter/TagsFilter';
import { QueryItem } from '../Tonnage/models/TonnageQueryModel';
import { MarketInfoType } from '../MarketInfo/models/MarketInfoQueryModel';
import { tonnageInputSelector } from '../Tonnage/reducers/TonnageInputReducer';
import StartEndDatepicker from '../StartEndDatepicker/StartEndDatepicker';
import { setUserDisplayNames, userSelectorSelector } from '../UserSelector/reducers/UserSelectorReducer';
import UserApi from '../UserManagement/api/UserApi';
import UserDisplayNameModel from '../UserSelector/models/UserDisplayNameModel';
import { SupportSheetStatus } from '../SupportSheet/models/SupportSheetModel';
import { EvaluationSearchEnum } from './models/EvaluationSearchEnum';
import { CompanyNamePICSelectorSelector, setCompanyNamePICSearchResults } from '../CompanyNamePICSelector/reducers/CompanyNamePICSelectorReducer';
import CompanyNamePICApi from '../CompanyNamePIC/api/CompanyNamePICApi';
import CompanyNamePICModel from '../CompanyNamePIC/models/CompanyNamePICModel';
import { CashAccountSelector, setCashAccounts } from '../Cash/reducers/CashAccountReducer';
import CashAccountApi from '../Cash/api/CashAccountApi';
import CashAccountModel from '../Cash/models/CashAccountModel';

const FixtureSearch: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const fixtureSearchData = useSelector(FixtureSearchSelector);
    const tonnageViewData = useSelector(tonnageInputSelector);
    const userSelectorData = useSelector(userSelectorSelector);
    const companyNamePICSelectorData = useSelector(CompanyNamePICSelectorSelector);
    const cashModelData = useSelector(CashAccountSelector);

    useEffect(() => {
        dispatch(showLoading(true));
        CashAccountApi.GetCashAccounts().then((response: BaseResponse<CashAccountModel[]>) => {
            if (response.success) {
                dispatch(setCashAccounts(response.data));

                dispatch(showLoading(false));
            } else {
                Utility.Alert.Error(response.message);
                dispatch(showLoading(false));
            }
        }).catch((error) => {
            Utility.Alert.Error(error);
            dispatch(showLoading(false));
        })
    }, []);

    useEffect(() => {
        if (companyNamePICSelectorData.searchResults.length > 0) {
            return;
        }

        dispatch(showLoading(true));
        CompanyNamePICApi.GetAll()
            .then((response: BaseResponse<CompanyNamePICModel[]>) => {
                if (response.success) {
                    dispatch(setCompanyNamePICSearchResults(response.data))
                } else {
                    Utility.Alert.Error(response.message);
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
            })
            .finally(() => {
                dispatch(showLoading(false));
            })
    }, []);

    useEffect(() => {
        if (userSelectorData.userDisplayNames.length === 0) {
            UserApi.GetDisplayNames()
                .then((response: BaseResponse<UserDisplayNameModel[]>) => {
                    if (response.success) {
                        dispatch(setUserDisplayNames(response.data))
                    } else {
                        Utility.Alert.Error(response.message);
                    }
                }).catch((error) => {
                    Utility.Alert.Error(error);
                })
        }
    }, [])

    const saveSavedSearch = () => {
        Swal.fire({
            title: 'Provide Saved Search Name',
            input: 'text',
            inputAttributes: {
                autocapitalize: 'off'
            },
            showCancelButton: true,
            confirmButtonText: 'Sumbit',
            showLoaderOnConfirm: true,
            allowOutsideClick: false,
            confirmButtonColor: '#1976d2',
            preConfirm: (name) => {
                if (!name || name.length === 0) {
                    Swal.showValidationMessage("Name ")
                }
            },
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(showLoading(true));

                SavedSearchApi.Save({
                    name: result.value ? result.value : "",
                    component: ComponentType.Fixture,
                    queryJson: JSON.stringify(fixtureSearchData.query)
                } as SaveSavedSearchParam)
                    .then((response: BaseResponse<SavedSearchModel>) => {
                        if (response.success) {
                            dispatch(addSavedSearch(response.data));
                            Utility.Alert.Success("Save Successful");
                        } else {
                            Utility.Alert.Error(response.message);
                        }
                    })
                    .catch((error) => {
                        Utility.Alert.Error(error);
                    })
                    .finally(() => {
                        dispatch(showLoading(false));
                    });
            }
        })
    }

    useEffect(() => {
        if (fixtureSearchData.triggerSearch === 0) {
            return;
        }

        dispatch(showLoading(true));

        FixtureApi.Search({
            query: fixtureSearchData.query
        } as SearchFixtureParam)
            .then((response: BaseResponse<FixtureModel[]>) => {
                if (response.success) {
                    dispatch(setFixtureSearchResults(response.data));
                    dispatch(setSearchResultTotalRecord(response.totalRecord));
                } else {
                    Utility.Alert.Error(response.message);
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
            })
            .finally(() => {
                dispatch(showLoading(false));
            });
    }, [fixtureSearchData.triggerSearch])

    return (
        <>
            <Grid container>
                <Grid item xs={2} className="fixture-save-search">
                    <SavedSearch
                        component={ComponentType.Fixture}
                    >
                    </SavedSearch>
                </Grid>
                <Grid item xs={10}>
                    <CustomAccordion
                        title="Search"
                        className="fixture-search-area"
                        defaultOpen={true}>
                        <Grid container>
                            <Grid xs={2} className="fixture-button">
                                <Grid container>
                                    <Grid xs={8}>
                                        <Tooltip title="Alt + W">
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                className="tonnage-search-save-button"
                                                onClick={() => {
                                                    dispatch(setTriggerFixtureSearch((new Date()).getTime()));
                                                }}
                                            >
                                                RUN SEARCH (w)
                                            </Button>
                                        </Tooltip>
                                    </Grid>
                                    <Grid xs={4}>
                                        <Tooltip title="Reset (Alt + Q)">
                                            <Button
                                                className="clear-input-button"
                                                color="success"
                                                variant="contained"
                                                onClick={() => {
                                                    dispatch(resetFixtureSearch());
                                                    dispatch(setTriggerFocusVesselName((new Date()).getTime()));
                                                }}
                                            >
                                                <i className="fa fa-refresh" aria-hidden="true"></i>
                                            </Button>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                                <Grid container>
                                    <Grid item xs={12} className="fixture-search-item">
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            onClick={() => {
                                                saveSavedSearch();
                                            }}
                                        >
                                            Save Search
                                        </Button>
                                    </Grid>
                                </Grid>
                                <FormControlLabel control={
                                    <Checkbox
                                        checked={fixtureSearchData.query.showCloseFile}
                                        onChange={(event) => {
                                            dispatch(setShowCloseFile(event.target.checked));
                                        }}
                                    />}
                                    className="tonnage-input-grabs"
                                    label="Show Closed File"
                                />
                            </Grid>
                            <Grid xs={10}>
                                <Grid container>
                                    <Grid xs={3}>
                                        <Grid container>
                                            <Grid item xs={6}>
                                                <TagsFilter
                                                    data={[
                                                        {
                                                            id: MarketInfoType.Voy,
                                                            value: MarketInfoType.Voy,
                                                            positive: true
                                                        } as QueryItem,
                                                        {
                                                            id: MarketInfoType.TCT,
                                                            value: MarketInfoType.TCT,
                                                            positive: true
                                                        } as QueryItem,
                                                        {
                                                            id: MarketInfoType.TC,
                                                            value: MarketInfoType.TC,
                                                            positive: true
                                                        } as QueryItem,
                                                        {
                                                            id: MarketInfoType.COA,
                                                            value: MarketInfoType.COA,
                                                            positive: true
                                                        } as QueryItem,

                                                    ]}
                                                    label="Biz Type"
                                                    className="fixture-search-item"
                                                    value={fixtureSearchData.query.bizTypes}
                                                    onValueChange={(value: QueryItem[]) => {
                                                        dispatch(setBizTypes(value));
                                                    }}
                                                ></TagsFilter>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TagsFilter
                                                    data={[
                                                        {
                                                            id: SupportSheetStatus.PreArrivalLoadPort,
                                                            value: SupportSheetStatus.PreArrivalLoadPort,
                                                            positive: true
                                                        } as QueryItem,
                                                        {
                                                            id: SupportSheetStatus.OnVoyage,
                                                            value: SupportSheetStatus.OnVoyage,
                                                            positive: true
                                                        } as QueryItem,
                                                        {
                                                            id: SupportSheetStatus.PendingLayTimeLTC,
                                                            value: SupportSheetStatus.PendingLayTimeLTC,
                                                            positive: true
                                                        } as QueryItem,
                                                        {
                                                            id: SupportSheetStatus.PreDelivery,
                                                            value: SupportSheetStatus.PreDelivery,
                                                            positive: true
                                                        } as QueryItem,
                                                        {
                                                            id: SupportSheetStatus.Delivered,
                                                            value: SupportSheetStatus.Delivered,
                                                            positive: true
                                                        } as QueryItem,
                                                        {
                                                            id: SupportSheetStatus.PendingFhs,
                                                            value: SupportSheetStatus.PendingFhs,
                                                            positive: true
                                                        } as QueryItem,
                                                        {
                                                            id: SupportSheetStatus.PendingCommission,
                                                            value: SupportSheetStatus.PendingCommission,
                                                            positive: true
                                                        } as QueryItem,
                                                        {
                                                            id: SupportSheetStatus.CloseFile,
                                                            value: SupportSheetStatus.CloseFile,
                                                            positive: true
                                                        } as QueryItem,
                                                        {
                                                            id: SupportSheetStatus.Cancel,
                                                            value: SupportSheetStatus.Cancel,
                                                            positive: true
                                                        } as QueryItem,
                                                        {
                                                            id: SupportSheetStatus.PendingDisputes,
                                                            value: SupportSheetStatus.PendingDisputes,
                                                            positive: true
                                                        } as QueryItem,
                                                        {
                                                            id: SupportSheetStatus.Blank,
                                                            value: SupportSheetStatus.Blank,
                                                            positive: true
                                                        } as QueryItem,
                                                    ]}
                                                    filterLimit={20}
                                                    label="Status"
                                                    className="fixture-search-item"
                                                    value={fixtureSearchData.query.status}
                                                    onValueChange={(value: QueryItem[]) => {
                                                        dispatch(setStatus(value));
                                                    }}
                                                ></TagsFilter>
                                            </Grid>
                                        </Grid>
                                        <TagsFilter
                                            data={_.map(userSelectorData.userDisplayNames, (item) => {
                                                return {
                                                    id: item.id.toString(),
                                                    value: item.displayName,
                                                    positive: true
                                                } as QueryItem
                                            })}
                                            useFreeText={true}
                                            label="Support"
                                            className="fixture-search-item"
                                            value={fixtureSearchData.query.supports}
                                            onValueChange={(value: QueryItem[]) => {
                                                dispatch(setSupports(value))
                                            }}
                                        ></TagsFilter>
                                        <TagsFilter
                                            data={[
                                                {
                                                    id: EvaluationSearchEnum.Blank,
                                                    value: EvaluationSearchEnum.Blank,
                                                    positive: true
                                                } as QueryItem,
                                                {
                                                    id: EvaluationSearchEnum.NeedSupportEva,
                                                    value: EvaluationSearchEnum.NeedSupportEva,
                                                    positive: true
                                                } as QueryItem,
                                                {
                                                    id: EvaluationSearchEnum.NeedManagerEva,
                                                    value: EvaluationSearchEnum.NeedManagerEva,
                                                    positive: true
                                                } as QueryItem,
                                                {
                                                    id: EvaluationSearchEnum.NeedBrokerEva,
                                                    value: EvaluationSearchEnum.NeedBrokerEva,
                                                    positive: true
                                                } as QueryItem,
                                                {
                                                    id: EvaluationSearchEnum.EvaComplete,
                                                    value: EvaluationSearchEnum.EvaComplete,
                                                    positive: true
                                                } as QueryItem,
                                            ]}
                                            label="Evaluations"
                                            className="fixture-search-item"
                                            value={fixtureSearchData.query.evaluations}
                                            onValueChange={(value: QueryItem[]) => {
                                                dispatch(setEvaluations(value));
                                            }}
                                        ></TagsFilter>
                                    </Grid>
                                    <Grid xs={3}>
                                        <TagsFilter
                                            data={_.map(tonnageViewData.tonnageNames, (item) => {
                                                return {
                                                    id: item.id,
                                                    value: item.vesselName,
                                                    positive: true
                                                } as QueryItem
                                            })}
                                            label="Vessel Name"
                                            className="fixture-search-item"
                                            value={fixtureSearchData.query.vesselNames}
                                            useFreeText={true}
                                            onValueChange={(value: QueryItem[]) => {
                                                dispatch(setVesselNames(value))
                                            }}
                                        ></TagsFilter>
                                        <TagsFilter
                                            data={_.map(companyNamePICSelectorData.searchResults, (item) => {
                                                return {
                                                    id: item.id,
                                                    value: item.companyName + " - " + item.pic,
                                                    positive: true
                                                } as QueryItem
                                            })}
                                            useFreeText={true}
                                            label="Owners"
                                            onInputChange={(text: string) => {
                                                dispatch(setFixtureCompanyNamePICSearchKeyword(text));
                                            }}
                                            className="fixture-search-item"
                                            value={fixtureSearchData.query.owners}
                                            onValueChange={(value: QueryItem[]) => {
                                                dispatch(setOwners(value))
                                            }}
                                        ></TagsFilter>
                                        <TagsFilter
                                            data={_.map(companyNamePICSelectorData.searchResults, (item) => {
                                                return {
                                                    id: item.id,
                                                    value: item.companyName + " - " + item.pic,
                                                    positive: true
                                                } as QueryItem
                                            })}
                                            useFreeText={true}
                                            label="Charterer"
                                            onInputChange={(text: string) => {
                                                dispatch(setFixtureCompanyNamePICSearchKeyword(text));
                                            }}
                                            className="fixture-search-item"
                                            value={fixtureSearchData.query.charterers}
                                            onValueChange={(value: QueryItem[]) => {
                                                dispatch(setCharterers(value))
                                            }}
                                        ></TagsFilter>
                                    </Grid>
                                    <Grid xs={3}>
                                        <TagsFilter
                                            data={_.map(companyNamePICSelectorData.searchResults, (item) => {
                                                return {
                                                    id: item.id,
                                                    value: item.companyName + " - " + item.pic,
                                                    positive: true
                                                } as QueryItem
                                            })}
                                            useFreeText={true}
                                            label="Collect From"
                                            onInputChange={(text: string) => {
                                                dispatch(setFixtureCompanyNamePICSearchKeyword(text));
                                            }}
                                            className="fixture-search-item"
                                            value={fixtureSearchData.query.collectFroms}
                                            onValueChange={(value: QueryItem[]) => {
                                                dispatch(setCollectFroms(value))
                                            }}
                                        ></TagsFilter>
                                        {/* <TextField
                                            fullWidth
                                            className="fixture-search-item fixture-bank-account"
                                            label="Bank Account"
                                            value={fixtureSearchData.query.bankAccount}
                                            onChange={(event) => {
                                            dispatch(setBankAccount(event.target.value));
                                            }}
                                        >
                                        </TextField> */}
                                        <Autocomplete
                                            className='cargo-input-component'
                                            options={_.map(cashModelData.cashAccounts, (cashAccount) => { return cashAccount.account; })}
                                            renderInput={(params) => <TextField {...params} label="Bank Account" />}
                                            value={fixtureSearchData.query.bankAccount}
                                            onChange={(event: React.SyntheticEvent, value: string | null, reason: string, details?: AutocompleteChangeDetails<string> | undefined) => {
                                                dispatch(setBankAccount(value ? value : ""));
                                            }}
                                        />
                                        <Grid container>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    className="fixture-search-item"
                                                    label="Inv No"
                                                    value={fixtureSearchData.query.invNo}
                                                    onChange={(event) => {
                                                        dispatch(setInvNo(event.target.value));
                                                    }}
                                                >
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TagsFilter
                                                    data={_.map(userSelectorData.userDisplayNames, (item) => {
                                                        return {
                                                            id: item.id.toString(),
                                                            value: item.displayName,
                                                            positive: true
                                                        } as QueryItem
                                                    })}
                                                    useFreeText={true}
                                                    label="Comm Rate Brokers"
                                                    className="fixture-search-item"
                                                    value={fixtureSearchData.query.commRateBrokers}
                                                    onValueChange={(value: QueryItem[]) => {
                                                        dispatch(setCommRateBrokers(value))
                                                    }}
                                                ></TagsFilter>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid xs={3}>
                                        <StartEndDatepicker
                                            startDateLabel="CP Date From"
                                            endDateLabel="CP Date To"
                                            className="fixture-search-item"
                                            useQuickEndDateInput={false}
                                            onStartDateChange={(date: string) => {
                                                dispatch(setCpDateFrom(date));
                                            }}
                                            onEndDateChange={(date: string) => {
                                                dispatch(setCpDateTo(date));
                                            }}
                                            startDateValue={fixtureSearchData.query.cpDateFrom}
                                            endDateValue={fixtureSearchData.query.cpDateTo}
                                            allowFreeInput={true}
                                        >
                                        </StartEndDatepicker>
                                        <StartEndDatepicker
                                            startDateLabel="Inv Date From"
                                            endDateLabel="Inv Date To"
                                            className="fixture-search-item"
                                            useQuickEndDateInput={false}
                                            onStartDateChange={(date: string) => {
                                                dispatch(setInvDateFrom(date));
                                            }}
                                            onEndDateChange={(date: string) => {
                                                dispatch(setInvDateTo(date));
                                            }}
                                            startDateValue={fixtureSearchData.query.invDateFrom}
                                            endDateValue={fixtureSearchData.query.invDateTo}
                                            allowFreeInput={true}
                                        >
                                        </StartEndDatepicker>
                                        <StartEndDatepicker
                                            startDateLabel="Received Date From"
                                            endDateLabel="Received Date To"
                                            className="fixture-search-item"
                                            useQuickEndDateInput={false}
                                            onStartDateChange={(date: string) => {
                                                dispatch(setReceivedDateFrom(date));
                                            }}
                                            onEndDateChange={(date: string) => {
                                                dispatch(setReceivedDateTo(date));
                                            }}
                                            startDateValue={fixtureSearchData.query.receivedDateFrom}
                                            endDateValue={fixtureSearchData.query.receivedDateTo}
                                            allowFreeInput={true}
                                        >
                                        </StartEndDatepicker>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </CustomAccordion>
                </Grid>
            </Grid>
        </>
    )
};

export default FixtureSearch;